import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { projectApi, type ProjectAct } from '@/api/projectApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import type { Project } from '@/types/nf'

type ActDraft = ProjectAct

function normalizeDraft(acts: ProjectAct[]): ActDraft[] {
  return acts.map((act) => ({
    ...act,
    midpoint_chapter_no: act.midpoint_chapter_no ?? null,
    enabled: act.enabled,
  }))
}

function setNumber(value: number | undefined): number {
  return value && Number.isFinite(value) ? value : 0
}

export function ActBoundaryPanel({ project, branchId }: { project: Project; branchId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const actsKey = useMemo(() => ['project-acts', project.id, branchId] as const, [project.id, branchId])
  const [draft, setDraft] = useState<ActDraft[]>([])

  const actsQuery = useQuery({
    queryKey: actsKey,
    queryFn: () => projectApi.getActs(project.id, branchId),
  })

  useEffect(() => {
    if (actsQuery.data) setDraft(normalizeDraft(actsQuery.data.acts))
  }, [actsQuery.data])

  const saveMutation = useMutation({
    mutationFn: (acts: ProjectAct[]) => projectApi.putActs(project.id, { acts }, branchId),
    onSuccess: async (response) => {
      setDraft(normalizeDraft(response.acts))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: actsKey }),
        queryClient.invalidateQueries({ queryKey: ['story-state', project.id] }),
      ])
      toast.success(t('acts.messages.saved'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('acts.messages.saveFailed'))),
  })

  const updateAct = (actNo: number, patch: Partial<ActDraft>) => {
    setDraft((current) => current.map((act) => (act.act_no === actNo ? { ...act, ...patch } : act)))
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    saveMutation.mutate(draft)
  }

  const persisted = actsQuery.data?.persisted ?? false

  return (
    <Card className="rounded-lg shadow-xs">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">{t('acts.title')}</CardTitle>
          <CardDescription>{t('acts.subtitle')}</CardDescription>
        </div>
        <Badge variant={persisted ? 'default' : 'secondary'}>
          {persisted ? t('acts.persisted') : t('acts.default')}
        </Badge>
      </CardHeader>
      <CardContent>
        {actsQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : actsQuery.error ? (
          <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
            {friendlyMessage(actsQuery.error, t('acts.loadFailed'))}
          </div>
        ) : draft.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
            {t('acts.empty')}
          </div>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            <div className="grid gap-3">
              {draft.map((act) => (
                <div key={act.act_no} className="rounded-lg border bg-background/40 p-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                    <div className="min-w-32 flex-1">
                      <p className="text-sm font-medium">{t('acts.actLabel', { act: act.act_no })}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t(`acts.roles.${act.beat_role}`, { defaultValue: act.beat_role })}
                      </p>
                    </div>
                    <div className="grid flex-[3] gap-3 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">{t('acts.fields.start')}</Label>
                        <NumberInput
                          min={1}
                          value={act.start_chapter_no}
                          onChange={(value) => updateAct(act.act_no, { start_chapter_no: setNumber(value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">{t('acts.fields.end')}</Label>
                        <NumberInput
                          min={1}
                          value={act.end_chapter_no}
                          onChange={(value) => updateAct(act.act_no, { end_chapter_no: setNumber(value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">{t('acts.fields.midpoint')}</Label>
                        <NumberInput
                          min={1}
                          value={act.act_no === 2 ? act.midpoint_chapter_no : null}
                          disabled={act.act_no !== 2}
                          onChange={(value) =>
                            updateAct(act.act_no, { midpoint_chapter_no: value === undefined ? null : value })
                          }
                        />
                      </div>
                    </div>
                    <label className="flex min-w-28 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                      <span>{t('acts.fields.enabled')}</span>
                      <Switch
                        checked={act.enabled}
                        onCheckedChange={(checked) => updateAct(act.act_no, { enabled: checked })}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saveMutation.isPending}>
                <Save className="size-4" />
                {t('acts.save')}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
